
/* ============================== src/hooks/useCommitTimeLog.js ============================== */
/**
 * Модуль сохранения часов из «Сводки» (Cabinet) и «Карточки задачи» (TaskModal).
 * Проверяет лимиты, блокировки и корректность дат. Экспортирует useCommitTimeLog.
 */
import { useCallback } from "react";
import { useConfirm } from "../context/ConfirmContext";
import { useDailyNormPraise } from "./useDailyNormPraise";
import { isDailyNormComplete } from "../utils/dailyNormPraise";
import { TODAY } from "../utils/date";
import {
  isLogBlockedByTaskStatus,
  STATUS_NEW_LOG_MESSAGE,
} from "../utils/taskTimeLog";
import {
  useAddTaskLogMutation,
  useUpdateTaskLogMutation,
} from "../api/queries/tasks";

export function useCommitTimeLog({ currentUser }) {
  const { confirm } = useConfirm();
  const addLogMutation = useAddTaskLogMutation();
  const updateLogMutation = useUpdateTaskLogMutation();

  const { praise, closePraise, maybeShowPraise, resetPraiseShown } =
    useDailyNormPraise({ currentUser });

  const commit = useCallback(
    async ({ task, entry }) => {
      if (isLogBlockedByTaskStatus(task)) {
        return {
          ok: false,
          reason: "validation",
          error: STATUS_NEW_LOG_MESSAGE,
        };
      }

      const hours = parseFloat(entry.hours);
      if (!hours || hours <= 0) {
        return {
          ok: false,
          reason: "validation",
          error: "Введите корректное количество часов",
        };
      }
      if (!entry.date) {
        return { ok: false, reason: "validation", error: "Укажите дату" };
      }
      if (entry.date > TODAY) {
        return {
          ok: false,
          reason: "validation",
          error: "Нельзя вносить часы за будущую дату",
        };
      }
      if (task.start && entry.date < task.start) {
        return {
          ok: false,
          reason: "validation",
          error: `Дата не может быть раньше начала задачи (${task.start})`,
        };
      }

      const body = {
        date: entry.date,
        hours,
        note: (entry.note || "").trim(),
      };

      const doMutate = (needsConfirmation) => {
        if (entry.editingLogId) {
          return updateLogMutation.mutateAsync({
            id: task.id,
            logId: entry.editingLogId,
            body,
            needsConfirmation,
          });
        }
        return addLogMutation.mutateAsync({
          id: task.id,
          body,
          needsConfirmation,
        });
      };

      let response;
      try {
        response = await doMutate(false);
      } catch (err) {
        if (err.status === 409 && err.details?.needsConfirmation) {
          const ok = await confirm({
            title: "Конфликт данных",
            message: err.message,
            confirmLabel: "Продолжить",
          });
          if (!ok) return { ok: false, reason: "cancelled" };
          response = await doMutate(true);
        } else {
          throw err;
        }
      }

      if (response && typeof response.totalAfter === "number") {
        const fact = response.totalAfter;
        const norm = response.dayNorm || 0;
        const empId = response.empId || currentUser?.id;
        if (!isDailyNormComplete({ fact, norm })) {
          if (empId && currentUser?.id === empId) {
            resetPraiseShown({ empId, date: entry.date });
          }
        } else if (empId) {
          maybeShowPraise({ empId, date: entry.date, fact, norm });
        }
      }

      return { ok: true };
    },
    [
      confirm,
      addLogMutation,
      updateLogMutation,
      currentUser,
      maybeShowPraise,
      resetPraiseShown,
    ],
  );

  return { commit, praise, closePraise };
}
